import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { JsonConvert } from 'json2typescript';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { LookupModel } from 'infrastructure-shared';
import { HrModuleType } from '../../../common/model/hr-module-type';
import { MasterDataTableNames } from '../model/master-data-table-names';
import { HrConfigurationService } from '../../../common/service/hr.configuration.service';
import { CodeTextUrlFactory } from '../model/code-text-url-factory';
import { CodeTextUrlModel } from '../model/code-text-url-model';
import { CodeTextDiscriminatorModel } from '../model/code-text-discriminator-model';
import { PayrollCodeTextModel } from '../model/payroll-code-text-model';
import { HrmDetailTypeQueryModelCT } from '../../hrm-type/model/hrm-detail-type-query-model';
import { PayrollCodeTextQueryModel } from '../model/payroll-code-text-query-model';
import { CodeTextDiscriminatorQueryModel } from '../model/code-text-discriminator-query-model';
import { DiscriminatorDefinitionModel } from '../model/discriminator-definition-model';
import { DiscriminatorGroup } from '../model/code-text-discriminator-values';
import { PerEmployeeCardAuthorityModel } from '../../../common/per-employee-cards/security/employee-card-authority-model';

@Injectable({
    providedIn: 'root',
})
export class CodeTextService {
    constructor(
        private httpClient: HttpClient,
        private codeTextUrlFactory: CodeTextUrlFactory,
        private configService: HrConfigurationService,
    ) {}

    public save(model, moduleType?: HrModuleType): Observable<any> {
        const urlModel = this.getUrlModel(model.discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}`;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<any>>(
                url,
                urlModel.port == HrModuleType.PAYROLL
                    ? converter.serialize(model, PayrollCodeTextModel)
                    : converter.serialize(model, CodeTextDiscriminatorModel),
            )
            .pipe(
                map((data) => {
                    return urlModel.port == HrModuleType.PAYROLL
                        ? converter.deserializeObject(data.body, PayrollCodeTextModel)
                        : converter.deserializeObject(data.body, CodeTextDiscriminatorModel);
                }),
            );
    }

    public getById(discriminator: string, id: number, moduleType?: HrModuleType): Observable<any> {
        const urlModel = this.getUrlModel(discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/${id}`;
        return this.httpClient.get<HvlResponse<any>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return urlModel.port == HrModuleType.PAYROLL
                    ? converter.deserializeObject(data.body, PayrollCodeTextModel)
                    : converter.deserializeObject(data.body, CodeTextDiscriminatorModel);
            }),
        );
    }

    public update(model, moduleType?: HrModuleType): Observable<any> {
        let urlModel: CodeTextUrlModel = this.getUrlModel(model.discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}`;
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<any>>(
                url,
                urlModel.port == HrModuleType.PAYROLL
                    ? converter.serialize(model, PayrollCodeTextModel)
                    : converter.serialize(model, CodeTextDiscriminatorModel),
            )
            .pipe(
                map((data) => {
                    return urlModel.port == HrModuleType.PAYROLL
                        ? converter.deserializeObject(data.body, PayrollCodeTextModel)
                        : converter.deserializeObject(data.body, CodeTextDiscriminatorModel);
                }),
            );
    }

    public queryPage(
        model: PayrollCodeTextQueryModel,
        moduleType?: HrModuleType,
    ): Observable<HvlPage<Array<CodeTextDiscriminatorModel>>> {
        const urlModel = this.getUrlModel(model.discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/page`;
        return this.httpClient.post<HvlResponse<HvlPage<CodeTextDiscriminatorModel[]>>>(url, model).pipe(
            map((hvlPage) => {
                if (urlModel.port == HrModuleType.PAYROLL) {
                    let model = [];
                    if (hvlPage.body?.totalElements !== 0) {
                        const converter = new JsonConvert();
                        model = converter.deserializeArray(hvlPage.body.data, PayrollCodeTextModel);
                    }
                    return new HvlPage<Array<PayrollCodeTextModel>>(
                        model,
                        hvlPage.body.totalElements,
                        hvlPage.body.totalPages,
                        hvlPage.errorDetail,
                    );
                } else {
                    let model = [];
                    if (hvlPage.body?.totalElements !== 0) {
                        const converter = new JsonConvert();
                        model = converter.deserializeArray(hvlPage.body.data, CodeTextDiscriminatorModel);
                    }
                    return new HvlPage<Array<CodeTextDiscriminatorModel>>(
                        model,
                        hvlPage.body.totalElements,
                        hvlPage.body.totalPages,
                        hvlPage.errorDetail,
                    );
                }
            }),
        );
    }

    public queryList(
        model: CodeTextDiscriminatorQueryModel,
        moduleType?: HrModuleType,
    ): Observable<Array<CodeTextDiscriminatorModel>> {
        const urlModel = this.getUrlModel(model.discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/query`;
        return this.httpClient.post<HvlResponse<Array<CodeTextDiscriminatorModel>>>(url, model).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data?.body || [], CodeTextDiscriminatorModel);
            }),
        );
    }

    public lookupQuery(model: PayrollCodeTextQueryModel, moduleType?: HrModuleType): Observable<Array<LookupModel>> {
        const urlModel = this.getUrlModel(model.discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/lookup/query`;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(url, converter.serialize(model, PayrollCodeTextQueryModel))
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data?.body || [], LookupModel);
                }),
            );
    }

    public deleteById(discriminator: string, id: number, moduleType?: HrModuleType): Observable<any> {
        const urlModel = this.getUrlModel(discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/${id}`;
        return this.httpClient.delete(url);
    }

    public lookup(discriminator: string, moduleType?: HrModuleType): Observable<Array<any>> {
        const urlModel = this.getUrlModel(discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/lookup/${discriminator}`;
        return this.httpClient.get<HvlResponse<Array<LookupModel>>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data?.body || [], LookupModel);
            }),
        );
    }

    public dynamicLookup(discriminator: string, moduleType: HrModuleType): Observable<Array<LookupModel>> {
        const urlModel = this.getUrlModel(moduleType, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/lookup/${discriminator}`;
        return this.httpClient.get<HvlResponse<Array<LookupModel>>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data?.body || [], LookupModel);
            }),
        );
    }

    public hrmTypelookupQuery(tableName: MasterDataTableNames): Observable<Array<LookupModel>> {
        const url = this.configService.config.hr.general + '/hrmDetailTypeService/hrmDetailType/lookupHrmTypeQuery';
        const queryModel = new HrmDetailTypeQueryModelCT();
        queryModel.tableName = tableName;
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(url, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter
                    .deserializeArray(data?.body || [], LookupModel)
                    .sort((a, b) => a.text?.localeCompare(b.text));
            }),
        );
    }

    public hrmTypelookupQueryFilteredByHrAuthorization(
        tableName: MasterDataTableNames,
        cardAuthority: PerEmployeeCardAuthorityModel,
        forEdit: boolean,
    ): Observable<Array<LookupModel>> {
        const url = this.configService.config.hr.general + '/hrmDetailTypeService/hrmDetailType/lookupHrmTypeQuery';
        const queryModel = new HrmDetailTypeQueryModelCT();
        queryModel.tableName = tableName;
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(url, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                const hrmTypes = converter
                    .deserializeArray(data?.body || [], LookupModel)
                    .sort((a, b) => a.text?.localeCompare(b.text));

                if (cardAuthority?.hasHrmTypeLimitations && forEdit) {
                    return hrmTypes.filter((item) =>
                        cardAuthority.hrmTypeLimitations.some((p) => p.hrmTypeUuid === item.uuid && p.canEdit),
                    );
                } else return hrmTypes;
            }),
        );
    }

    public hrmTypelookupQueryWithObjectCode(tableName: MasterDataTableNames): Observable<Array<LookupModel>> {
        const url =
            this.configService.config.hr.general +
            '/hrmDetailTypeService/hrmDetailType/lookupHrmTypeQueryWithObjectCode';
        const queryModel = new HrmDetailTypeQueryModelCT();
        queryModel.tableName = tableName;
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(url, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter
                    .deserializeArray(data?.body || [], LookupModel)
                    .sort((a, b) => a.text?.localeCompare(b.text));
            }),
        );
    }

    public findMaxOrderLevelByDiscriminator(discriminator: string, moduleType?: HrModuleType): Observable<number> {
        const urlModel = this.getUrlModel(discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/orderLevel/${discriminator}`;
        return this.httpClient.get<HvlResponse<number>>(url).pipe(
            map((data) => {
                return data.body;
            }),
        );
    }

    public existsByDiscriminatorAndOrderLevelAndIsNotId(
        orderLevel: number,
        discriminator: string,
        excludedId: number,
        moduleType?: HrModuleType,
    ): Observable<Boolean> {
        const urlModel = this.getUrlModel(discriminator, moduleType);
        const url = `${this.getBaseUrl(urlModel)}${
            urlModel.baseUrl
        }/exists/orderLevel/${discriminator}/${orderLevel}/${excludedId}`;
        return this.httpClient.get<HvlResponse<Boolean>>(url).pipe(
            map((data) => {
                return data.body;
            }),
        );
    }

    public getDiscriminatorDefinitions(
        moduleType: HrModuleType,
        group?: DiscriminatorGroup,
    ): Observable<Array<DiscriminatorDefinitionModel>> {
        const urlModel = this.getUrlModel(null, moduleType);
        const url =
            `${this.getBaseUrl(urlModel)}${urlModel.baseUrl}/discriminatorDefinitions` + (!!group ? '/' + group : '');
        return this.httpClient.get<HvlResponse<Array<DiscriminatorDefinitionModel>>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data?.body || [], DiscriminatorDefinitionModel);
            }),
        );
    }

    public getByIdCodeTextHrGeneral(id: number): Observable<CodeTextDiscriminatorModel> {
        const url = this.configService.config.hr.general + '/generalCodeTextService/generalCodeText/' + id;
        return this.httpClient.get<HvlResponse<CodeTextDiscriminatorModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, CodeTextDiscriminatorModel);
            }),
        );
    }

    public getByCodeAndDiscriminator(
        code: String,
        discriminator: String,
    ): Observable<CodeTextDiscriminatorModel>{
        const url = this.configService.config.hr.general + '/generalCodeTextService/generalCodeText/getByCodeAndDiscriminator/' + code+'/'+discriminator;
        return this.httpClient.get<HvlResponse<CodeTextDiscriminatorModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, CodeTextDiscriminatorModel);
            }),
        );
    }


    private getBaseUrl(urlModel: CodeTextUrlModel): string {
        if (urlModel.port === HrModuleType.PAYROLL) {
            return this.configService.config.hr.payroll;
        } else if (urlModel.port === HrModuleType.PERSONNEL) {
            return this.configService.config.hr.personnel;
        } else if (urlModel.port === HrModuleType.GENERAL) {
            return this.configService.config.hr.general;
        } else if (urlModel.port === HrModuleType.PERFORMANCE) {
            return this.configService.config.hr.performance;
        } else if (urlModel.port === HrModuleType.RECRUITMENT) {
            return this.configService.config.hr.recruitment;
        } else if (urlModel.port === HrModuleType.APPLICATION) {
            return this.configService.config.hr.application;
        } else if (urlModel.port === HrModuleType.EDUCATION) {
            return this.configService.config.hr.education;
        }
    }

    private getUrlModel(discriminator: string, moduleType: HrModuleType): CodeTextUrlModel {
        let urlModel: CodeTextUrlModel;
        if (!moduleType) urlModel = this.codeTextUrlFactory.getCodeTextUrlModel(discriminator);
        else
            urlModel = {
                baseUrl: `/${moduleType}CodeTextService/${moduleType}CodeText`,
                port: moduleType,
            };
        return urlModel;
    }
}

export function sortCodeTextByOrderLevelThenLongText(values: CodeTextDiscriminatorModel[]) {
    return values.sort((a, b) => {
        if (a.orderLevel === b.orderLevel) {
            if (a.longText && b.longText) {
                return a.longText.toLowerCase() === b.longText.toLowerCase()
                    ? 0
                    : a.longText.toLowerCase() > b.longText.toLowerCase()
                    ? 1
                    : -1;
            } else {
                return 0;
            }
        }
        return a.orderLevel > b.orderLevel ? 1 : -1;
    });
}
